import { readonly, Ref, ref } from 'vue'

import {
  IncidentAttributeId,
  IncidentsQueuePatchDTO,
} from '@collector/sportsapi-client-legacy'
import { UpdateIncident } from '@mobile/ActionQueue/Actions/Actions'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

type UseIncidentDetailsForm = {
  addAttribute: (attributeId: IncidentAttributeId) => void
  removeAttribute: (attributeId: IncidentAttributeId) => void
  updateIncident: () => void
  toggleAttribute: (attributeId: IncidentAttributeId) => void
  isSomeAttributeSelected: (attributes: IncidentAttributeId[]) => boolean
  selectedAttributes: Readonly<Ref<readonly IncidentAttributeId[]>>
}

export function useIncidentDetailsForm(
  incident: ProbableIncident,
): UseIncidentDetailsForm {
  // composables
  const { actionQueue, probableEvent } = useEventInjections()
  const event_id = probableEvent.value.id

  // data
  const [minute, second] = incident.event_time?.split(':') ?? ['00', '00']
  const time = {
    minute,
    second,
  }

  // state
  const selectedAttributes = ref<IncidentAttributeId[]>(incident.attribute_ids)

  // methods
  function toggleAttribute(attributeId: IncidentAttributeId): void {
    const index = selectedAttributes.value.indexOf(attributeId)
    if (index > -1) {
      selectedAttributes.value.splice(index, 1)
    } else {
      selectedAttributes.value.push(attributeId)
    }
  }

  function addAttribute(attributeId: IncidentAttributeId): void {
    if (!selectedAttributes.value.includes(attributeId)) {
      selectedAttributes.value.push(attributeId)
    }
  }

  function removeAttribute(attributeId: IncidentAttributeId): void {
    const index = selectedAttributes.value.indexOf(attributeId)
    if (index > -1) {
      selectedAttributes.value.splice(index, 1)
    }
  }

  function isSomeAttributeSelected(attributes: IncidentAttributeId[]): boolean {
    return attributes.some((attr) => selectedAttributes.value.includes(attr))
  }

  function updateIncident(dto?: Partial<IncidentsQueuePatchDTO>): void {
    actionQueue.value.add(
      new UpdateIncident(incident.id, {
        incident_id: incident.incident_id,
        event_id,
        status_id: incident.event_status_id,
        attribute_ids: selectedAttributes.value,
        participant_team_id: incident.participant_id,
        participant_id: incident.subparticipant_id,
        participant_name: incident.subparticipant_id
          ? incident.subparticipant_name
          : '',
        assistant_id: incident.additional_info?.assistant_id,
        assistant_name: incident.additional_info?.assistant_name,
        secondary_assistant_id:
          incident.additional_info?.secondary_assistant_id,
        secondary_assistant_name:
          incident.additional_info?.secondary_assistant_name,
        minute: Number(time.minute),
        second: Number(time.second),
        ...dto,
      }),
    )
  }

  return {
    addAttribute,
    removeAttribute,
    updateIncident,
    toggleAttribute,
    isSomeAttributeSelected,
    selectedAttributes: readonly(selectedAttributes),
  }
}
